import React, { useEffect, useRef } from "react";
import cls from "./styles.module.scss";

const HEIGHT = 320;

type WaveCfg = {
  offsets: number[];
  amps: number[];
  baseOffset: number;
  alpha: number;
};

const WavesBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let frame = 0;

    const cfgs: WaveCfg[] = [
      {
        offsets: [0, 1.2, 2.4, 3.6],
        amps: [0.06, 0.1, 0.08, 0.09],
        baseOffset: 0,
        alpha: 0.05,
      },
      {
        offsets: [2, 3.3, 1.7, 4.1],
        amps: [0.08, 0.13, 0.09, 0.11],
        baseOffset: 25,
        alpha: 0.08,
      },
      {
        offsets: [4.5, 2.2, 5.1, 3.4],
        amps: [0.1, 0.15, 0.12, 0.13],
        baseOffset: 50,
        alpha: 0.12,
      },
    ];

    let lastTs = 0;
    let phase = 0;

    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const parent = canvas.parentElement;
      if (!parent) return;

      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1)); // cap to reduce GPU pressure
      const w = Math.floor(parent.clientWidth);
      const h = Math.floor(parent.clientHeight);

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);

      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawWave = (
      ctx: CanvasRenderingContext2D,
      w: number,
      base: number,
      cfg: WaveCfg
    ) => {
      const xs = [w * 0.22, w * 0.48, w * 0.76];

      const ys = cfg.offsets.map((ph, idx) => {
        const sine =
          Math.sin(phase * (1 + idx * 0.04) + ph) * 0.85 +
          Math.sin(phase * 0.5 + ph) * 0.15;

        return base + cfg.baseOffset + sine * (HEIGHT * cfg.amps[idx]);
      });

      ctx.beginPath();
      ctx.moveTo(0, HEIGHT);
      ctx.lineTo(0, ys[0]);

      ctx.bezierCurveTo(xs[0] * 0.5, ys[0], xs[0] * 0.5, ys[1], xs[0], ys[1]);
      ctx.bezierCurveTo(
        (xs[0] + xs[1]) * 0.5,
        ys[1],
        (xs[0] + xs[1]) * 0.5,
        ys[2],
        xs[1],
        ys[2]
      );
      ctx.bezierCurveTo(
        (xs[1] + xs[2]) * 0.5,
        ys[2],
        (xs[1] + xs[2]) * 0.5,
        ys[3],
        xs[2],
        ys[3]
      );
      ctx.bezierCurveTo(
        (xs[2] + w) * 0.5,
        ys[3],
        (xs[2] + w) * 0.5,
        ys[0],
        w,
        ys[0]
      );

      ctx.lineTo(w, HEIGHT);
      ctx.closePath();

      ctx.globalAlpha = cfg.alpha;
      ctx.fillStyle = "#FFB800";
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    const animate = (ts: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (!lastTs) lastTs = ts;
      let dt = (ts - lastTs) / 1000;
      lastTs = ts;

      if (dt > 0.05) dt = 0.05;

      // stable time-based speed
      phase += 0.36 * dt;

      // skip heavy work if hidden
      if (document.visibilityState !== "visible") {
        frame = requestAnimationFrame(animate);
        return;
      }

      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      ctx.clearRect(0, 0, w, h);

      const scaleY = h / HEIGHT;

      ctx.save();
      ctx.scale(1, scaleY);

      const base = HEIGHT * 0.45;

      for (const cfg of cfgs) {
        drawWave(ctx, w, base, cfg);
      }

      ctx.restore();

      frame = requestAnimationFrame(animate);
    };

    resize();
    frame = requestAnimationFrame(animate);

    const onResize = () => resize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div className={cls.container}>
      <canvas ref={canvasRef} className={cls.canvas} />
    </div>
  );
};

export default WavesBackground;
